/**
 * TemplateCombobox - Reusable template selector component
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronsUpDown, FileText } from 'lucide-react';

export default function TemplateCombobox({
  templates,
  value,
  onChange,
  placeholder = "Select template...",
  testId,
  className = ''
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredTemplates = templates.filter(t => 
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedTemplate = templates.find(t => t.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${className}`}
          data-testid={testId}
        >
          {selectedTemplate ? (
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {selectedTemplate.name}
            </span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search templates..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                <span className="text-slate-400">None (blank)</span>
              </CommandItem>
              {filteredTemplates.map((template) => (
                <CommandItem
                  key={template.id}
                  onSelect={() => {
                    onChange(template.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`w-4 h-4 mr-2 ${
                      value === template.id ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{template.name}</div>
                    {template.subject && (
                      <div className="text-xs text-slate-400 truncate">
                        {template.subject}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
